import { IconQuestionMark } from "@tabler/icons-react";

export enum AvatarSize {
  xl = "xl",
  l = "l",
  base = "base",
  s = "s",
  xs = "xs",
}

export enum AvatarColor {
  white = "white",
  pink = "pink",
  green = "green",
  blue = "blue",
  amber = "amber",
  purple = "purple",
  red = "red",
  gray = "gray",
}

export interface AvatarProps {
  name?: string;
  uid?: string;
  src?: string;
  size?: AvatarSize | `${AvatarSize}`;
  color?: AvatarColor | `${AvatarColor}`;
}

const backgroundColors: Record<AvatarColor, string> = {
  white: "bg-badge-white-bg",
  pink: "bg-badge-pink-bg",
  green: "bg-badge-green-bg",
  blue: "bg-badge-blue-bg",
  amber: "bg-badge-amber-bg",
  purple: "bg-badge-purple-bg",
  red: "bg-badge-red-bg",
  gray: "bg-badge-gray-bg",
};

const dimensions: Record<AvatarSize, string> = {
  xl: "h-12 w-12",
  l: "h-10 w-10",
  base: "h-8 w-8",
  s: "h-6 w-6",
  xs: "h-5 w-5",
};

const typography: Record<AvatarSize, string> = {
  xl: "typography-large-base",
  l: "typography-medium-base",
  base: "typography-small-base",
  s: "text-[10px] font-normal",
  xs: "text-[8px] font-normal",
};

const Avatar = ({
  name, uid, src, size = AvatarSize.base, color,
}: AvatarProps) => {
  const generateLetters = (t: string) => {
    const p = t.split(" ");
    return (
      p.length > 1 && p[0] && p[1]
        ? p[0].at(0) + p[1].at(0)
        : p[0].substring(0, 2)
    ).toUpperCase();
  };

  const generateColor = (seed: string, color?: AvatarColor) => {
    if (color) return backgroundColors[color];
    const colors = Object.values(AvatarColor);
    if (!seed) return backgroundColors[colors[0]];
    const a = seed
      .split("")
      .reduce((acc, curr) => acc + curr.charCodeAt(0), "");
    const i = parseInt(a, 10) % colors.length;
    return backgroundColors[colors[i]];
  };

  return (
    <div className={`rounded-full border border-border-base overflow-hidden p-0.5 ${dimensions[size]}`}>
      { src ? (
        <img
          className="object-cover w-full h-full"
          src={src}
          alt={`avatar ${name || uid}`}
          aria-label={`avatar ${name || uid}`}
        />
      ) : (
        <div
          className={`rounded-full w-full h-full ${generateColor(name || uid, color as AvatarColor)}`}
          aria-label={`avatar ${name || uid}`}
        >
          <div className={`flex w-full h-full justify-center items-center text-text-subtle ${typography[size]}`}>
            { name ? generateLetters(name) : (<IconQuestionMark />) }
          </div>
        </div>
      )}
    </div>
  );
};

export default Avatar;
